import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { fmtQty } from '@/lib/quantity'

/**
 * Inline reorder-level editor. Lived only on the Alerts tab before, which
 * meant the threshold was editable everywhere EXCEPT where its effect (the
 * status chip) is actually seen — now shared by the Items row, the item
 * drawer, and Alerts.
 */
export function ReorderLevelPopover({ item, showToast, triggerLabel }: {
  item: { id: string; name: string; reorder_level: number }
  showToast: (t: 'success' | 'error', m: string) => void
  /** Defaults to the current level; pass e.g. "Reorder lvl" for a labelled button. */
  triggerLabel?: string
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(String(item.reorder_level))
  const mut = useMutation({
    mutationFn: () => apiClient.updateStockItem(item.id, { reorder_level: +value }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockItem', item.id] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      showToast('success', `Reorder level for ${item.name} set to ${value}`)
      setOpen(false)
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to update reorder level'),
  })
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setValue(String(item.reorder_level)) }}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" className="text-xs h-7 shrink-0 text-muted-foreground tabular-nums">
          <Pencil className="w-3 h-3 mr-1" />
          {triggerLabel ?? fmtQty(item.reorder_level)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 space-y-2" align="end">
        <p className="text-xs text-muted-foreground">
          Warn when on-hand falls to this level. 0 means never warn for low stock.
        </p>
        <div className="flex items-center gap-2">
          <Input type="number" step="any" min="0" value={value} onChange={(e) => setValue(e.target.value)} className="h-8" />
          <Button size="sm" className="h-8" disabled={mut.isPending || value === '' || +value < 0} onClick={() => mut.mutate()}>
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
